import { Inject, Injectable, OnDestroy } from '@angular/core';

import { bureauRoutes } from '@app/bureau/bureau-routes';
import { BureauPaymentPeriodsSearchDataProvider } from '@app/bureau/payments/payment-periods/entity-grid/data-provider/bureau-payment-periods-search.data-provider';
import { HashMap } from '@datorama/akita';
import { FeatureFlagService, FeatureFlagsServiceInjectionToken, FeatureFlagType } from '@feature-flags/feature-flag.service';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, switchMap } from 'rxjs';
import { MenuItem, TopMenuItem } from '../../../../component-library/layout/menu-item/menu-interfaces';
import { SidebarMenuConfig } from '../../../../component-library/layout/sidebar/sidebar-menu.interface';

@Injectable({ providedIn: 'root' })
export class BureauNavigationService implements OnDestroy {
  private navigationUpdated$ = new BehaviorSubject<boolean | undefined>(undefined);
  private paymentNotificationSubscription: Subscription | null = null;

  private salaryPaymentPeriodsNavigationLink = {
    label: 'Salary payment schedule',
    route: bureauRoutes.salaryPaymentPeriods,
    feature: 'BureauPayments',
    section: 'SalaryPaymentSchedule',
    hasNotification: false
  };
  private paymentsNavigationLink = {
    label: ' Payments',
    feature: 'BureauPayments',
    defaultCollapsed: true,
    icon: 'credit-card',
    hasNotification: false
  };

  constructor(
    private activeSecurity: ActiveSecurityContextStateService,
    private salaryPaymentPeriodsSearchStateService: BureauPaymentPeriodsSearchDataProvider,
    @Inject(FeatureFlagsServiceInjectionToken) private featureFlagService: FeatureFlagService
  ) {}

  getSidebarMenuConfig$(): Observable<SidebarMenuConfig> {
    return combineLatest([this.featureFlagService.getAllValues$(), this.navigationUpdated$]).pipe(
      switchMap(([featureFlags]) => this.buildSidebarMenuConfig(featureFlags))
    );
  }

  private buildSidebarMenuConfig = (featureFlags: HashMap<FeatureFlagType>): Observable<SidebarMenuConfig> => {
    return new Observable<SidebarMenuConfig>((observer) => {
      const emitSidebarConfig = () => {
        const sidebarConfig = this.getSidebarMenuConfig(featureFlags);
        observer.next(sidebarConfig);
      };

      emitSidebarConfig();

      const subscription = this.navigationUpdated$.subscribe((shouldEmit) => {
        if (shouldEmit) {
          emitSidebarConfig();
        }
      });

      return () => subscription.unsubscribe();
    });
  };

  private getSidebarMenuConfig = (featureFlags: HashMap<FeatureFlagType>): SidebarMenuConfig => {
    const sections: TopMenuItem[] = [];
    const mainItems: MenuItem[] = [];
    const settings: MenuItem[] = [];

    const isBureauAdmin = this.activeSecurity.hasApplicationAuthorityTo('EditBureaux');
    const isSupportUser = this.activeSecurity.hasApplicationAuthorityTo('IsCintraSupport');

    if (this.activeSecurity.hasBureauProfileAuthorityTo('ViewBureauOrganisations') || isBureauAdmin) {
      mainItems.push({
        label: 'Organisations',
        route: bureauRoutes.organisations,
        icon: 'buildings',
        type: 'MenuLink',
        feature: 'BureauOrganisations'
      } as any);
    }

    const shouldShowPaymentsLink = this.activeSecurity.hasBureauProfileAuthorityTo('ViewBureauOrganisations') || isBureauAdmin;

    if (shouldShowPaymentsLink) {
      this.setupPaymentNotifications();

      let paymentStatusList = [];

      paymentStatusList.push({
        label: 'Payment status',
        route: bureauRoutes.paymentStatus,
        feature: 'BureauPayments',
        section: 'PaymentStatus'
      });

      paymentStatusList.push({
        ...this.salaryPaymentPeriodsNavigationLink,
        hasNotification: this.salaryPaymentPeriodsNavigationLink.hasNotification
      });

      paymentStatusList.push({
        label: 'Liability payment schedule',
        route: bureauRoutes.liabilityPaymentPeriods,
        feature: 'BureauPayments',
        section: 'LiabilityPaymentSchedule',
        hasNotification: false
      });

      const paymentSectionItems: MenuItem[] = paymentStatusList.map(
        (payment) =>
          ({
            label: payment.label,
            route: payment.route,
            type: 'MenuLink',
            feature: payment.feature,
            section: payment.section,
            hasNotification: payment.hasNotification
          }) as any
      );

      sections.push({
        title: this.paymentsNavigationLink.label,
        icon: this.paymentsNavigationLink.icon,
        collapsible: true,
        collapsed: this.paymentsNavigationLink.defaultCollapsed,
        items: paymentSectionItems,
        hasNotification: this.salaryPaymentPeriodsNavigationLink.hasNotification
      });
    } else {
      this.cleanupPaymentNotificationSubscription();
    }

    if (this.activeSecurity.hasBureauProfileAuthorityTo('ViewBureauReports') || isSupportUser) {
      mainItems.push({
        label: 'Reports',
        route: bureauRoutes.reports,
        icon: 'chart-line',
        type: 'MenuLink',
        feature: 'BureauReports'
      } as any);
    }

    if (this.activeSecurity.hasBureauProfileAuthorityTo('AccessBACS') || isSupportUser) {
      mainItems.push({
        label: 'Bank files',
        route: bureauRoutes.bankFiles,
        icon: 'bank',
        type: 'MenuLink',
        feature: 'BankFiles'
      } as any);
    }

    if (
      (this.activeSecurity.hasBureauProfileAuthorityTo('IsBureauUser') || isBureauAdmin || isSupportUser) &&
      featureFlags['pensionConnect'] === true
    ) {
      mainItems.push({
        label: 'Pension submissions',
        route: bureauRoutes.pensionConnect,
        icon: 'link',
        type: 'MenuLink',
        feature: 'BureauPensionConnect'
      } as any);
    }

    if (this.activeSecurity.hasBureauProfileAuthorityTo('EditBureauUsers') || isBureauAdmin) {
      settings.push({
        label: 'Users',
        route: bureauRoutes.users,
        type: 'MenuLink',
        feature: 'BureauSettings',
        section: 'Users'
      } as any);
    }

    if (
      this.activeSecurity.activeBureau?.teamsEnabled &&
      (this.activeSecurity.hasBureauProfileAuthorityToAllOf(['IsBureauTeamUser', 'EditBureauTeams']) || isBureauAdmin)
    ) {
      settings.push({
        label: 'Teams',
        route: bureauRoutes.teams,
        type: 'MenuLink',
        feature: 'BureauSettings',
        section: 'Teams'
      } as any);
    }

    if (this.activeSecurity.hasBureauProfileAuthorityTo('EditBureauUsers') || isBureauAdmin) {
      settings.push({
        label: 'Configuration',
        route: bureauRoutes.configuration,
        type: 'MenuLink',
        feature: 'BureauSettings',
        section: 'Configuration'
      } as any);
    }

    if (mainItems.length > 0) {
      sections.unshift({
        items: mainItems
      });
    }

    if (settings.length > 0) {
      sections.push({
        title: 'Settings',
        icon: 'gear',
        collapsible: true,
        collapsed: true,
        items: settings
      });
    }

    return {
      sections,
      bottomNavItems: this.fetchBottomNavItems()
    };
  };

  private fetchBottomNavItems = (): MenuItem[] => {
    const bottomNavItems: MenuItem[] = [];

    const isBureauAdmin = this.activeSecurity.hasApplicationAuthorityTo('EditBureaux');

    if (this.activeSecurity.hasBureauProfileAuthorityTo('ViewBureauOrganisations') || isBureauAdmin) {
      bottomNavItems.push({
        label: 'Home',
        route: 'bureau/organisations',
        icon: 'house',
        feature: 'BureauOrganisations',
        type: 'MenuLink'
      } as any);
    }

    return bottomNavItems;
  };

  private setupPaymentNotifications(): void {
    if (!this.paymentNotificationSubscription) {
      this.paymentNotificationSubscription = this.salaryPaymentPeriodsSearchStateService
        .getAnyOutstandingSalaryPayments$()
        .subscribe((hasOutstandingSalaryPayments) => {
          this.salaryPaymentPeriodsNavigationLink.hasNotification = hasOutstandingSalaryPayments;
          this.paymentsNavigationLink.hasNotification = hasOutstandingSalaryPayments;

          this.navigationUpdated$.next(true);
        });
    }
  }

  private cleanupPaymentNotificationSubscription(): void {
    if (this.paymentNotificationSubscription) {
      this.paymentNotificationSubscription.unsubscribe();
      this.paymentNotificationSubscription = null;
      this.salaryPaymentPeriodsNavigationLink.hasNotification = false;
      this.navigationUpdated$.next(true);
    }
  }

  ngOnDestroy(): void {
    this.cleanupPaymentNotificationSubscription();
  }
}
